import { storage } from "../utils/storage";

const APIUrl = process.env.REACT_APP_API_BASE_URL;

// Export the Azure Blob Storage configuration.
export const azureBlobStorageConfig = {
    accountName: process.env.REACT_APP_AZUREBLOBSTORAGE_ACCOUNTNAME as string,
    containerName: process.env.REACT_APP_AZUREBLOBSTORAGE_CONTAINERNAME as string,
    sasToken: process.env.REACT_APP_AZUREBLOBSTORAGE_SASTOKEN as string,
    clientId: process.env.REACT_APP_AZUREBLOBSTORAGE_CLIENTID as string ,
    tenantId: process.env.REACT_APP_AZUREBLOBSTORAGE_TENANTID as string 
};

export function apiUrl(endpoint: string): string {
    let url = `${APIUrl}${endpoint}`;
    return url;
}

type Headers = {
    'Content-Type': string;
    Authorization?: string;
    refreshToken?: string;
};

function sanitizeResponseText(raw: string): string {
    return raw
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

async function parseApiResponse(response: Response): Promise<any> {
    const contentType = response.headers.get("content-type") ?? "";
    const isJsonResponse = contentType.toLowerCase().includes("application/json");

    if (isJsonResponse) {
        try {
            return await response.json();
        } catch {
            const fallbackText = sanitizeResponseText(await response.text());
            return {
                statusCode: response.status,
                statusMessage: fallbackText || "Received invalid JSON response from server.",
                data: null,
                isNonJsonResponse: true,
            };
        }
    }

    const responseText = sanitizeResponseText(await response.text());
    return {
        statusCode: response.status,
        statusMessage: responseText || response.statusText || "Received non-JSON response from server.",
        data: null,
        isNonJsonResponse: true,
    };
}

export async function post(url: string, data: any, withToken: boolean = true): Promise<any> {
    const headers: Headers = {
        'Content-Type': 'application/json',
    };
    if (withToken) {
        headers.Authorization = `Bearer ${storage.getToken()}`;
    }
    return fetch(apiUrl(url), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    }).then(parseApiResponse);
}

export async function patch(url: string, data: any, withToken: boolean = true): Promise<any> {
    const headers: Headers = {
        'Content-Type': 'application/json',
    };
    if (withToken) {
        headers.Authorization = `Bearer ${storage.getToken()}`;
    }
    return fetch(apiUrl(url), {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
    }).then(parseApiResponse);
}

export async function put(url: string, data: any, withToken: boolean = true): Promise<any> {
    const headers: Headers = {
        'Content-Type': 'application/json',
    };
    if (withToken) {
        headers.Authorization = `Bearer ${storage.getToken()}`;
    }
    return fetch(apiUrl(url), {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
    }).then(parseApiResponse);
}

export async function get(url: string): Promise<any> {
    const response = await fetch(apiUrl(url), {
        headers: {
            Authorization: `Bearer ${storage.getToken()}`,
            refreshToken: `${storage.getRefreshToken()}`
        },
    });
    return parseApiResponse(response);
}

export async function deleteRequest(url: string, withToken: boolean = true): Promise<any> {
    const headers: Headers = {
        'Content-Type': 'application/json',
    };
    if (withToken) {
        headers.Authorization = `Bearer ${storage.getToken()}`;
    }
    return fetch(apiUrl(url), {
        method: 'DELETE',
        headers,
    }).then(parseApiResponse);
}